import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Confirm from "../../../components/Confirm";
import Popup from "../../../components/Popup";
import {
  Badge,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  StatusBadge,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import {
  apiMessage,
  deleteAdmin,
  getAdminDetails,
  getAdminList,
  getPermissionsCatalog,
  resetAdminPassword,
  updateAdmin,
} from "../../../services/admins";
import { getDate } from "../../../utils/format";
import { matches, newestFirst } from "../../../utils/list";
import {
  currentSession,
  isSuperuser,
  sessionRoles,
} from "../../../utils/roles";
import AdminFormModal, { ADMIN_ROLES } from "./AdminFormModal";
import TemporaryPasswordDialog from "./TemporaryPasswordDialog";
import "../users/user-list.css";

const roleLabel = (role) => role.replace(/\b\w/g, (c) => c.toUpperCase());

const AdminList = () => {
  const { t } = useTranslation();
  const isSuperAdminSession = sessionRoles().includes("super admin");
  const myId = currentSession()._id;
  // Only a super admin names system admins (the API checks it too).
  const assignableRoles = isSuperAdminSession
    ? [...ADMIN_ROLES, "system admin"]
    : ADMIN_ROLES;
  const [catalog, setCatalog] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  // undefined: closed, null: create, object: edit
  const [editedAdmin, setEditedAdmin] = useState(undefined);
  // { type: "status" | "delete", admin }
  const [pendingAction, setPendingAction] = useState(null);
  const [temporary, setTemporary] = useState(null); // { admin, password } when it could not be emailed

  const loadAdmins = () =>
    getAdminList().then((result) => {
      setAdmins(newestFirst(result) || []);
      setLoading(false);
    });

  useEffect(() => {
    loadAdmins();
    getPermissionsCatalog()
      .then(setCatalog)
      .catch(() => toast.error(t("Error fetching data")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () =>
      admins.filter(
        (admin) =>
          (matches(admin?.name, search) || matches(admin?.email, search)) &&
          (role === "all" || admin?.role?.some((r) => r.includes(role))),
      ),
    [admins, search, role],
  );
  const pagination = usePagination(filtered, 10, [search, role]);

  const openEdit = (adminId) => {
    getAdminDetails(adminId).then(
      (details) => details && setEditedAdmin(details),
    );
  };

  const changeStatus = (admin) => {
    const status = admin.status === "active" ? "inactive" : "active";
    updateAdmin({ status }, admin._id)
      .then(() => {
        toast.success(t("Admin's Status Updated  Successfully!"), {
          progressStyle: { background: "var(--iq-primary)" },
        });
        loadAdmins();
      })
      .catch((error) =>
        toast.error(apiMessage(error) || t("Error updating data")),
      );
  };

  /** New password for the admin by email; every session of this admin is closed. */
  const resetPassword = async (admin) => {
    try {
      const result = await resetAdminPassword(admin._id);
      if (result.emailSent) toast.success(t("A temporary password was emailed to {{email}}. Their sessions are closed.", { email: admin.email }));
      else setTemporary({ admin, password: result.temporaryPassword });
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    }
  };

  /** After a creation: credentials emailed, or shown once when the email failed. */
  const afterSave = (result) => {
    if (result?.temporaryPassword) setTemporary({ admin: result.savedAdmin, password: result.temporaryPassword });
    else if (result?.emailSent) toast.info(t("Sign-in details emailed to {{email}}", { email: result.savedAdmin?.email }));
  };

  const removeAdmin = async (admin) => {
    try {
      await deleteAdmin(admin._id);
      toast.success("Admin Deleted Successfully!", {
        progressStyle: { background: "var(--iq-primary)" },
      });
      loadAdmins();
    } catch (error) {
      toast.error(apiMessage(error) || t("Error updating data"));
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Administrators")}
        subtitle={loading ? "\u00a0" : `${admins.length} ${t("admins")}`}
        actions={
          <button
            type="button"
            className="ui-btn"
            onClick={() => setEditedAdmin(null)}
          >
            <i className="ri-add-line" aria-hidden="true"></i>
            {t("Add Admin")}
          </button>
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search by name or email")}
          />
          <select
            className="ui-input"
            style={{ width: "auto", height: 38 }}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            aria-label={t("role")}
          >
            <option value="all">{t("All roles")}</option>
            {[...ADMIN_ROLES, "system admin", "super admin"].map((r) => (
              <option key={r} value={r}>
                {t(roleLabel(r))}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-admin-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Account")}</th>
                    <th>{t("phone number")}</th>
                    <th>{t("Roles")}</th>
                    <th>{t("Registered")}</th>
                    <th>{t("status")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((admin) => {
                    // System and super admin accounts are managed by a super admin only;
                    // nobody deactivates or deletes their own account.
                    const isSelf = admin._id === myId;
                    const editable =
                      isSuperAdminSession || !isSuperuser(admin.role || []);
                    return (
                      <tr key={admin._id}>
                        <td>
                          <div className="ui-media">
                            <span className="users-avatar" aria-hidden="true">
                              {(admin.name || "?").charAt(0).toUpperCase()}
                            </span>
                            <div style={{ minWidth: 0 }}>
                              <span className="ui-media-title">
                                {admin.name || "N/A"}
                              </span>
                              <span className="ui-media-meta">
                                {admin.email || "N/A"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="ui-cell-muted">
                          {admin.phone || "N/A"}
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 6,
                              maxWidth: 320,
                            }}
                          >
                            {(admin.role || []).map((r) => (
                              <Badge
                                key={r}
                                color={r === "super admin" ? "amber" : "blue"}
                              >
                                {t(roleLabel(r))}
                              </Badge>
                            ))}
                            {admin.permissions &&
                              !isSuperuser(admin.role || []) && (
                                <Badge color="violet">
                                  <i
                                    className="ri-equalizer-line"
                                    aria-hidden="true"
                                  ></i>
                                  {t("Custom access")}
                                </Badge>
                              )}
                          </div>
                        </td>
                        <td className="ui-cell-muted">
                          {admin.createdAt ? getDate(admin.createdAt) : "N/A"}
                        </td>
                        <td>
                          <StatusBadge active={admin.status === "active"} />
                        </td>
                        <td className="ui-cell-actions">
                          {editable && (
                            <>
                              {!isSelf && (
                                <IconButton
                                  icon={
                                    admin.status === "active"
                                      ? "ri-user-unfollow-line"
                                      : "ri-user-follow-line"
                                  }
                                  label={
                                    admin.status === "active"
                                      ? t("Deactivate")
                                      : t("Activate")
                                  }
                                  onClick={() =>
                                    setPendingAction({ type: "status", admin })
                                  }
                                />
                              )}
                              <IconButton
                                icon="ri-pencil-line"
                                label={t("Edit")}
                                onClick={() => openEdit(admin._id)}
                              />
                              {!isSelf && (
                                <IconButton
                                  icon="ri-key-2-line"
                                  label={t("Reset the password")}
                                  onClick={() =>
                                    setPendingAction({ type: "reset", admin })
                                  }
                                />
                              )}
                              {!isSelf && (
                                <IconButton
                                  icon="ri-delete-bin-line"
                                  label={t("Delete")}
                                  tone="danger"
                                  onClick={() =>
                                    setPendingAction({ type: "delete", admin })
                                  }
                                />
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {editedAdmin !== undefined && (
        <AdminFormModal
          admin={editedAdmin}
          assignableRoles={assignableRoles}
          catalog={catalog}
          onClose={() => setEditedAdmin(undefined)}
          onSaved={(result) => {
            setEditedAdmin(undefined);
            loadAdmins();
            afterSave(result);
          }}
        />
      )}
      {pendingAction?.type === "status" && (
        <Confirm
          open={true}
          setOpen={() => setPendingAction(null)}
          confirmHandle={() => changeStatus(pendingAction.admin)}
        >
          <div className="model-wrapper">
            {t("Are you sure you want to change the status?")}
          </div>
        </Confirm>
      )}
      {pendingAction?.type === "reset" && (
        <Confirm
          open={true}
          setOpen={() => setPendingAction(null)}
          confirmHandle={() => resetPassword(pendingAction.admin)}
        >
          <div className="model-wrapper">
            {t("Reset the password of {{name}}? A temporary password is emailed to them and all their sessions are closed.", { name: pendingAction.admin.name || pendingAction.admin.email })}
          </div>
        </Confirm>
      )}
      {temporary && (
        <TemporaryPasswordDialog admin={temporary.admin} password={temporary.password} onClose={() => setTemporary(null)} />
      )}
      {pendingAction?.type === "delete" && (
        <Popup
          open={true}
          setOpen={() => setPendingAction(null)}
          deleteHandle={() => removeAdmin(pendingAction.admin)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete this Admin?")}
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default AdminList;
